// constants
import { StoreStorageSyncKeys } from 'components/mrp/mrp.consts';

// types
import { EntityStore } from './entityStore.types';

// utils
import dayjs from 'dayjs';
import { createWithEqualityFn } from 'zustand/traditional';
import { requests } from 'RestAPIs/rapini';
import showSnackbar from 'components/shared/Snackbar';
import { getMatchingEntityIdentifiers } from 'components/mrp/mrp.utils';
import { t } from 'components/roster-portal/activity-planning/utils';
import { shallow } from 'zustand/shallow';
import { persist } from 'zustand/middleware';

export const useEntityStore = createWithEqualityFn<EntityStore>()(
  persist(
    (set, get) => ({
      subscribers: [],
      data: [],
      hasHydrated: false,
      hasAnyLoading: false,
      control: {
        isSubscribed: (planningEntityIdentifiers) => {
          return (
            getMatchingEntityIdentifiers(
              planningEntityIdentifiers,
              get().subscribers
            ).length > 0
          );
        },
        subscribe: (planningEntityIdentifiers) => {
          if (
            !planningEntityIdentifiers ||
            planningEntityIdentifiers.length === 0
          )
            return null;

          set((state) => {
            const newSubscribers = planningEntityIdentifiers.map((pei) => ({
              ...pei,
              isLoading: false,
              isError: false
            }));

            if (shallow(state.subscribers, newSubscribers)) return state;

            if (
              getMatchingEntityIdentifiers(
                planningEntityIdentifiers,
                state.subscribers
              ).length === 0
            ) {
              const uniqueNewSubscribers = newSubscribers.filter(
                (newSub) =>
                  getMatchingEntityIdentifiers([newSub], state.subscribers)
                    .length === 0
              );
              return {
                ...state,
                subscribers: [...state.subscribers, ...uniqueNewSubscribers]
              };
            }
            return state;
          });
        },
        unsubscribe: (planningEntityIdentifiers) => {
          set((state) => {
            if (
              getMatchingEntityIdentifiers(
                planningEntityIdentifiers,
                state.subscribers
              ).length > 0
            ) {
              return {
                ...state,
                subscribers: state.subscribers.filter(
                  (identifier) =>
                    !getMatchingEntityIdentifiers(
                      [identifier],
                      planningEntityIdentifiers
                    )
                )
              };
            }
            return state;
          });
        },
        fetch: async ({
          planningEntityIdentifiers: requested,
          startDate,
          endDate
        }) => {
          const { subscribers } = get();
          const planningEntityIdentifiers = requested || subscribers;

          if (planningEntityIdentifiers.length === 0 || !startDate || !endDate)
            return;

          // Ensure we only update for entities actually subscribed
          const refetchPlanningEntityIdentifiers = getMatchingEntityIdentifiers(
            get().subscribers,
            planningEntityIdentifiers
          ).map(({ entityId }) => entityId);

          set((state) => ({
            ...state,
            subscribers: state.subscribers.map((subscriber) => ({
              ...subscriber,
              ...(refetchPlanningEntityIdentifiers.includes(
                subscriber.entityId
              ) && {
                isLoading: true,
                isError: false
              })
            }))
          }));

          try {
            const response = await requests.getPlanning({
              planningEntityIdentifiers,
              startDate: dayjs(startDate).format('YYYY-MM-DD'),
              endDate: dayjs(endDate).format('YYYY-MM-DD')
            });

            const responsePlanningEntityIdentifiers =
              getMatchingEntityIdentifiers(
                get().subscribers,
                response.planningEntities.map(
                  ({ planningEntityIdentifier }) => planningEntityIdentifier
                )
              ).map(({ entityId }) => entityId);

            // Replace data only for entities that were fetched
            const newEntities = response.planningEntities.filter(
              ({ type }) => type !== 'EMPLOYMENT'
            );

            const fetchedEntityIds = planningEntityIdentifiers.map(
              (identifier) => identifier.entityId
            );

            // Keep data for entities that:
            // 1. Weren't part of this fetch AND
            // 2. Are still subscribed (to prevent unbounded memory growth)
            const currentSubscribers = get().subscribers;
            const existingData = get().data;
            const unfetchedData = existingData.filter(
              (entity) =>
                !fetchedEntityIds.includes(
                  entity.planningEntityIdentifier.entityId
                ) &&
                getMatchingEntityIdentifiers(
                  [entity.planningEntityIdentifier],
                  currentSubscribers
                ).length > 0
            );

            const refreshedData = [...unfetchedData, ...newEntities];

            set((state) => ({
              ...state,
              data: refreshedData,
              subscribers: state.subscribers.map((subscriber) => ({
                ...subscriber,
                ...(responsePlanningEntityIdentifiers.includes(
                  subscriber.entityId
                ) && {
                  isLoading: false,
                  isError: false
                })
              }))
            }));
          } catch {
            showSnackbar({
              severity: 'error',
              message: t('somethingWentWrong')
            });

            set((state) => ({
              ...state,
              subscribers: state.subscribers.map((subscriber) => ({
                ...subscriber,
                ...(refetchPlanningEntityIdentifiers.includes(
                  subscriber.entityId
                ) && {
                  isLoading: false,
                  isError: true
                })
              }))
            }));
          }
        },
        setAnyLoading: (hasAnyLoading: boolean) =>
          set((state) => {
            return {
              ...state,
              hasAnyLoading
            };
          })
      }
    }),
    {
      name: StoreStorageSyncKeys.ENTITY_STORE,
      partialize: (state) => ({
        subscribers: state.subscribers,
        data: state.data
      }),
      onRehydrateStorage: (state) => {
        state.hasHydrated = true;
      },
      version: 1
    }
  ),
  shallow
);
